<template>
  <el-dialog
    :visible.sync="visible"
    width="960px"
    append-to-body
    :before-close="handleClose"
    class="lookalike-dialog"
    :show-close="true"
    @opened="openInit"
  >
    <template slot="title">
      <div>
        <span class="text-lg font-semibold text-gray-800">新建相似细分受众群 (Lookalike)</span>
        <p class="mt-2 text-sm text-gray-400">仅适用于需求开发广告系列类型</p>
      </div>
    </template>

    <div class="flex gap-6">
      <!-- 左侧表单 -->
      <div class="flex-1 pr-6 border-r border-gray-200">
        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          label-position="top"
        >
          <el-form-item
            label="细分受众群名称"
            prop="name"
          >
            <el-input
              v-model="form.name"
              class="w-full"
              placeholder="请输入名称"
            />
          </el-form-item>

          <el-form-item
            prop="seedUserListIds"
            class="seed-form-item"
          >
            <template slot="label">
              <div class="flex justify-between items-center w-full">
                <span>种子名单 (数据源)</span>
                <span class="text-xs text-gray-400 font-normal">已选 {{ form.seedUserListIds.length }}/10</span>
              </div>
            </template>
            <p class="text-xs text-gray-400 mb-3 leading-relaxed">
              可选择多个现有列表（最多10个），系统将寻找与这些用户的综合特征相似的新用户。
            </p>
            <el-select
              v-model="form.seedUserListIds"
              multiple
              filterable
              placeholder="请选择种子名单"
              class="w-full"
              :multiple-limit="10"
            >
              <el-option
                v-for="item in seedUserList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            label="目标国家/地区"
            prop="locations"
            required
          >
            <el-select
              v-model="form.locations"
              multiple
              filterable
              placeholder="请选择目标国家/地区"
              class="w-full"
              value-key="id"
            >
              <el-option
                v-for="item in countryList"
                :key="item.criteria_id"
                :label="item.name_zhcn"
                :value="{ id: item.criteria_id, code: item.country_code, name: item.name_zhcn }"
              />
            </el-select>
          </el-form-item>

          <el-form-item prop="expansionLevel">
            <template slot="label">
              <span class="text-sm text-gray-600 font-medium">受众覆盖面 (相似度)</span>
              <el-tooltip
                content="覆盖面越窄，相似度越高；覆盖面越广，受众规模越大"
                placement="top"
              >
                <i class="el-icon-question ml-1 text-gray-400 cursor-pointer"></i>
              </el-tooltip>
            </template>
            <div class="flex border border-gray-300 rounded-lg overflow-hidden">
              <div
                v-for="level in expansionLevels"
                :key="level.value"
                :class="[
                  'flex-1 py-4 px-3 text-center cursor-pointer transition-all border-r border-gray-300 last:border-r-0',
                  'hover:bg-gray-50',
                  form.expansionLevel === level.value ? 'bg-blue-50 border-blue-500' : '',
                ]"
                @click="form.expansionLevel = level.value"
              >
                <div
                  :class="[
                    'text-sm font-medium mb-1',
                    form.expansionLevel === level.value ? 'text-blue-500' : 'text-gray-800',
                  ]"
                >
                  {{ level.label }}
                </div>
                <div class="text-xs text-gray-400">{{ level.desc }}</div>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- 右侧配置摘要 -->
      <div class="w-60 flex-shrink-0">
        <div class="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-4">
          <i class="el-icon-s-operation text-base"></i>
          <span>配置摘要</span>
        </div>
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="mb-4">
            <div class="text-xs text-gray-400 mb-1">已选种子名单</div>
            <div class="text-sm text-blue-500 flex items-center gap-1">
              <i class="el-icon-user mr-2"></i>
              <span>{{ form.seedUserListIds.length }} 个名单</span>
            </div>
          </div>
          <div class="mb-4">
            <div class="text-xs text-gray-400 mb-1">目标国家/地区</div>
            <div class="text-sm text-gray-800">{{ form.locations.length }} 个地区</div>
          </div>
          <div>
            <div class="text-xs text-gray-400 mb-1">扩展层级</div>
            <div class="text-sm text-gray-800 flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
              <span>{{ currentExpansionLabel }}</span>
            </div>
          </div>
        </div>
        <p class="mt-4 text-xs text-gray-400 leading-relaxed">
          创建相似受众后，系统可能需要 24-48 小时来完成用户匹配和数据填充。
        </p>
      </div>
    </div>

    <div
      slot="footer"
      class="text-right"
    >
      <el-button @click="handleClose">取 消</el-button>
      <el-button
        type="primary"
        @click="sure"
        >创建细分受众群</el-button
      >
    </div>
  </el-dialog>
</template>

<script>
import { createUserList, userListAudiences } from '@/api/google/create';

export default {
  name: 'LookalikeDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => {},
    },
    audienceData: {
      type: Object,
      default: () => {},
    },
    currentAccount: {
      type: Object,
      default: () => {},
    },
    enumConstants: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      form: {
        name: '',
        seedUserListIds: [],
        locations: [],
        expansionLevel: 'BALANCED',
      },
      rules: {
        name: [{ required: true, message: '请输入细分受众群名称', trigger: 'blur' }],
        seedUserListIds: [{ required: true, message: '请选择种子名单', trigger: 'change' }],
        locations: [{ required: true, message: '请选择目标国家/地区', trigger: 'change' }],
      },
      expansionLevels: [
        { value: 'NARROW', label: '窄 (2.5%)', desc: '最相似' },
        { value: 'BALANCED', label: '平衡 (5%)', desc: '推荐' },
        { value: 'BROAD', label: '广 (10%)', desc: '最大覆盖' },
      ],
      seedUserList: [],
    };
  },
  computed: {
    accountId() {
      return this.currentAccount?.id;
    },
    countryList() {
      return this.enumConstants?.countries || [];
    },
    currentExpansionLabel() {
      const level = this.expansionLevels.find((l) => l.value === this.form.expansionLevel);
      return level ? level.label : '';
    },
  },
  methods: {
    async openInit() {
      this.resetForm();
      this.$nextTick(()=>{
        
        let list = this.audienceData.target.locations.map((item) => {
        return {
          id: item.criteria_id,
          code: item.country_code,
          name: item.name_zhcn,
        };
      });
      this.$set(this.form,'locations',list)
      })
      await this.getSeedUserList();
    },
    async getSeedUserList() {
      try {
        const res = await userListAudiences({ accountId: this.accountId });
        const list = [];
        (res.data || []).forEach((group) => {
          if (group.userListAudiences) {
            group.userListAudiences.forEach((item) => {
              list.push({
                id: item.id,
                name: item.name,
                resourceName: item.resourceName,
              });
            });
          }
        });
        this.seedUserList = list;
      } catch (error) {
        console.error('获取种子用户列表失败:', error);
      }
    },
    sure() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          const params = {
            name: this.form.name,
            accountId: this.accountId,
            userListType: 'LOOKALIKE',
            lookalikeUserList: {
              seedUserListIds: this.form.seedUserListIds,
              locations: this.form.locations,
              expansionLevel: this.form.expansionLevel,
            },
          };
          this.$showLoading();
          createUserList(params)
            .then((res) => {
              this.$hideLoading();
              if (res.code == 0) {
                this.$message.success('创建相似受众成功');
                this.$emit('confirm', res.data);
                this.handleClose();
              }
            })
            .catch((error) => {
              this.$hideLoading();
              console.error('创建相似受众失败:', error);
            });
        }
      });
    },
    handleClose() {
      this.$emit('update:visible', false);
      this.resetForm();
    },
    resetForm() {
      this.form = {
        name: '',
        seedUserListIds: [],
        locations: [],
        expansionLevel: 'BALANCED',
      };
      this.$nextTick(() => {
        this.$refs.form?.clearValidate();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.lookalike-dialog {
  :deep(.el-dialog) {
    border-radius: 12px;
    overflow: hidden;
  }

  :deep(.el-dialog__header) {
    padding: 24px 24px 0;
    border-bottom: none;
  }

  :deep(.el-dialog__body) {
    padding: 16px 24px 24px;
  }

  :deep(.el-dialog__footer) {
    padding: 16px 24px 24px;
    border-top: 1px solid #ebeef5;
  }

  :deep(.el-dialog__headerbtn) {
    top: 24px;
    right: 24px;
  }
}

.seed-form-item {
  :deep(.el-form-item__label) {
    width: 100%;
    display: flex;
  }
}
</style>
